import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Tag, Modal, Switch, Typography, message, Tooltip } from "antd";
import { PlusOutlined, ReloadOutlined, InfoCircleOutlined, EyeOutlined, AppstoreAddOutlined } from "@ant-design/icons";
import { ButtonItem, ListaItems, ModalItem } from "../../../../../Components/Items";
import { WA } from "../../theme";
import {
  etiquetaEstado,
  colorEstado,
  etiquetaModelo,
  CATEGORIAS,
  analizarPlantilla,
  valoresEjemplo,
  PreviewPlantilla,
  motivoRechazo,
} from "../Helpers/plantillasHelpers";
import EditorPlantilla from "../Editores/EditorPlantilla";

const { Text } = Typography;

const etiquetaCategoria = (c) => CATEGORIAS.find((x) => x.value === c)?.label || c;

// Administra las plantillas de Meta 
const BibliotecaPlantillas = ({ headersToken, empresaId }) => {
  const [plantillas, setPlantillas] = useState([]);
  const [baseFaltantes, setBaseFaltantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorVisible, setEditorVisible] = useState(false);
  const [guardandoId, setGuardandoId] = useState(null);
  const [creandoBase, setCreandoBase] = useState(false);
  const [vista, setVista] = useState(null); // plantilla en vista previa
  const pollRef = useRef(null);

  const headers = { headers: headersToken };

  const cargar = () => {
    setLoading(true);
    axios
      .post("whatsapp/plantillaListar", {}, headers)
      .then((r) => {
        if (r.data.success === 2) {
          setPlantillas(r.data.plantillas || []);
          setBaseFaltantes(r.data.plantillasBaseFaltantes || []);
        } else message.error(r.data.message || "No se pudieron cargar las plantillas.");
      })
      .catch(() => message.error("Error de red al cargar plantillas."))
      .finally(() => setLoading(false));
  };

  const iniciarPolling = () => {
    let intentos = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      intentos += 1;
      axios
        .post("whatsapp/plantillaListar", {}, headers)
        .then((r) => {
          if (r.data.success !== 2) return;
          setPlantillas(r.data.plantillas || []);
          const faltan = r.data.plantillasBaseFaltantes || [];
          setBaseFaltantes(faltan);
          if (faltan.length === 0 || intentos >= 8) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setCreandoBase(false);
            if (faltan.length === 0) message.success("Plantillas Biwo dadas de alta.");
          }
        })
        .catch(() => {});
    }, 4000);
  };


  const darDeAltaBase = () => {
    setCreandoBase(true);
    axios
      .post("whatsapp/plantillasBaseCrear", {}, headers)
      .then((r) => {
        if (r.data.success === 2) {
          message.info("Dando de alta las plantillas Biwo en segundo plano. Aparecerán en unos momentos.");
          iniciarPolling();
        } else {
          message.error(r.data.message || "No se pudieron dar de alta las plantillas Biwo.");
          setCreandoBase(false);
        }
      })
      .catch(() => {
        message.error("Error de red al dar de alta las plantillas.");
        setCreandoBase(false);
      });
  };

  useEffect(() => {
    cargar();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Habilita/deshabilita una plantilla (no se elimina; solo deja de ofrecerse en el chat).
  const toggle = (pl, habilitar) => {
    setGuardandoId(pl.name);
    axios
      .post("whatsapp/plantillaToggleDeshabilitada", { name: pl.name, deshabilitar: !habilitar }, headers)
      .then((r) => {
        if (r.data.success === 2) {
          setPlantillas((prev) => prev.map((p) => (p.name === pl.name ? { ...p, deshabilitada: !habilitar } : p)));
        } else {
          message.error(r.data.message || "No se pudo actualizar.");
        }
      })
      .catch(() => message.error("Error de red."))
      .finally(() => setGuardandoId(null));
  };

  const infoEliminar = () =>
    Modal.info({
      title: "¿Cómo eliminar una plantilla?",
      content:
        "Las plantillas solo pueden eliminarse desde el Administrador de WhatsApp (WhatsApp Manager) de Meta. " +
        "Desde aquí puedes deshabilitarlas: una plantilla deshabilitada deja de aparecer para enviarse, pero sigue existiendo en Meta.",
      okText: "Entendido",
      zIndex: 1300,
    });

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Tooltip title="¿Cómo eliminar una plantilla?">
          <ButtonItem danger icon={<InfoCircleOutlined />} onClick={infoEliminar} style={{ marginLeft: "auto" }} />
        </Tooltip>
        <Tooltip title="Recargar plantillas">
          <ButtonItem turquesa icon={<ReloadOutlined />} onClick={cargar} />
        </Tooltip>
        {baseFaltantes.length > 0 && (
          <ButtonItem
            {...(creandoBase ? { gris: true } : { morado: true })}
            icon={<AppstoreAddOutlined />}
            loading={creandoBase}
            disabled={creandoBase}
            onClick={darDeAltaBase}
          >
            Dar de alta plantillas Biwo ({baseFaltantes.length})
          </ButtonItem>
        )}
        <ButtonItem cyan icon={<PlusOutlined />} onClick={() => setEditorVisible(true)}>
          Nueva plantilla
        </ButtonItem>
      </div>

      <ListaItems
        dataSource={plantillas}
        loading={loading}
        rowKey="name"
        vacioTexto="Aún no hay plantillas. Crea una con 'Nueva plantilla'."
        titulo={(pl) => (
          <span style={{ color: WA.textPrimary, opacity: pl.deshabilitada ? 0.5 : 1 }}>
            {pl.name}{" "}
            <Tag color={colorEstado(pl.status)} style={{ marginLeft: 6 }}>{etiquetaEstado(pl.status)}</Tag>
            {pl.modelo && <Tag color={WA.turquesa}>{etiquetaModelo(pl.modelo)}</Tag>}
          </span>
        )}
        descripcion={(pl) => (
          <>
            <Text style={{ fontSize: 12, color: WA.textSecondary }}>
              {etiquetaCategoria(pl.category)} · {pl.language}
            </Text>
            {pl.status === "REJECTED" && pl.rejected_reason && pl.rejected_reason !== "NONE" && (
              <div style={{ fontSize: 12, color: "#f5222d", marginTop: 2 }}>
                Motivo de rechazo: {motivoRechazo(pl.rejected_reason)}
              </div>
            )}
          </>
        )}
        acciones={(pl) => [
          <Tooltip key="prev" title="Vista previa">
            <ButtonItem rosa icon={<EyeOutlined />} onClick={() => setVista(pl)} />
          </Tooltip>,
          <Tooltip key="sw" title={pl.deshabilitada ? "Deshabilitada (no se ofrece)" : "Habilitada"}>
            <Switch
              checkedChildren="On"
              unCheckedChildren="Off"
              checked={!pl.deshabilitada}
              loading={guardandoId === pl.name}
              onChange={(checked) => toggle(pl, checked)}
            />
          </Tooltip>,
        ]}
      />

      <EditorPlantilla
        visible={editorVisible}
        headersToken={headersToken}
        empresaId={empresaId}
        onCancel={() => setEditorVisible(false)}
        onGuardado={() => { setEditorVisible(false); cargar(); }}
      />

      <ModalItem
        title={`Vista previa: ${vista?.name || ""}`}
        visible={!!vista}
        onCancel={() => setVista(null)}
        width={420}
        zIndex={1300}
        destroyOnClose
        spinning={false}
      >
        {vista && (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>Así se ve la plantilla con datos de ejemplo:</Text>
            <div style={{ border: `1px solid ${WA.border}`, borderRadius: 8, padding: 12, marginTop: 6, backgroundColor: WA.bg }}>
              <PreviewPlantilla plantilla={vista} valores={valoresEjemplo(vista, analizarPlantilla(vista))} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <ButtonItem gris onClick={() => setVista(null)}>Cerrar</ButtonItem>
            </div>
          </>
        )}
      </ModalItem>
    </>
  );
};

export default BibliotecaPlantillas;
